import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './InvitePage.css';

const InvitePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [names, setNames] = useState<string[]>([]);
  const [animationStage, setAnimationStage] = useState(0);

  useEffect(() => {
    // Get names from query parameters
    const nameParams = searchParams.getAll('name');
    setNames(nameParams);
  }, [searchParams]);

  const handleEnvelopeClick = () => {
    if (animationStage === 0) {
      // Stage 1: Open envelope and pull out letter
      setAnimationStage(1);
      
      // Stage 2: Envelope exits to the right
      setTimeout(() => {
        setAnimationStage(2);
      }, 1000);
    
    }
  };

  // Create RSVP URL with pre-filled names
  const getRSVPUrl = () => {
    const params = new URLSearchParams();
    names.forEach(name => params.append('name', name));
    return `/rsvp?${params.toString()}`;
  };

  // Format names for display
  const formatNames = () => {
    if (names.length === 0) return 'Dear Guest';
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} and ${names[1]}`;
    return names.slice(0, -1).join(', ') + ', and ' + names[names.length - 1];
  };

  return (
    <>
      <Helmet>
        <title>You have an invite - Michael and Ashlynn's Wedding</title>
        <meta name="description" content="You have a wedding invitation from Michael and Ashlynn!" />
        
        {/* Open Graph Meta Tags for special invite preview */}
        <meta property="og:title" content="💌 You have an invite!" />
        <meta property="og:description" content="Michael and Ashlynn have sent you a wedding invitation" />
        <meta property="og:image" content={`${window.location.origin}/invitation.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Michael and Ashlynn's Wedding" />
        <meta property="og:url" content={window.location.href} />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="💌 You have an invite!" />
        <meta name="twitter:description" content="Michael and Ashlynn have sent you a wedding invitation" />
        <meta name="twitter:image" content={`${window.location.origin}/invitation.png`} />
      </Helmet>
      
      <main className="page">
        <div className="invite-animation-container">
          {/* Personalized Message */}
          <div className="personalized-message">
            <h1>Dear {formatNames()},</h1>
            <p>You have a special invitation waiting for you below!</p>
            <p>Click on the envelope to open your invitation.</p>
            <div className={`rsvp-link-container ${animationStage >= 2 ? 'fade-in' : ''}`}>
              <Link to={getRSVPUrl()} className="rsvp-link">
                RSVP Now
              </Link>
            </div>
          </div>

          {/* Envelope with triangle flaps */}
          <div 
            className={`envelope ${animationStage >= 1 ? 'open' : ''} ${animationStage >= 2 ? 'exit-right' : ''}`}
            onClick={handleEnvelopeClick}
          >
            <div className="back"></div>
            <img 
              src="/invitation.png" 
              className={`invitation ${animationStage >= 1 ? 'open' : ''} ${animationStage >= 2 ? 'exit-right' : ''}`}
              alt="" 
            />
            <div className="triangle top"></div>
            <div className="triangle right"></div>
            <div className="triangle bottom"></div>
            <div className="triangle left"></div>
          </div>
        </div>
      </main>
    </>
  );
};

export default InvitePage; 